import React, { useState, useRef, useCallback } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useCreateMoleImageWithChoiceMutation } from '../../../store/apis/skinMolesApi';
import { getBodyPartName } from '../utils';
import ImageCropper from './ImageCropper';

/**
 * Modal component for creating new moles with image upload
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether modal is visible
 * @param {Function} props.onHide - Handler to close modal
 * @param {string} props.selectedBodyPart - Pre-selected body part code
 * @param {Object} props.bodyParts - Body parts data for display names
 * @param {boolean} props.bodyPartsLoading - Whether body parts are loading
 * @param {Function} props.onClearBodyPart - Handler to clear selected body part
 * @param {Function} props.onMoleCreated - Optional callback when mole is created successfully
 */
const MoleCreationModal = ({
  show,
  onHide,
  selectedBodyPart,
  bodyParts = {},
  bodyPartsLoading = false,
  onClearBodyPart,
  onMoleCreated,
}) => {
  const { t, i18n } = useTranslation();

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    orientation: '',
    body_part: selectedBodyPart || '',
    file: null,
    distinctive_features: '',
    description: '',
    approximate_size_mm: '',
  });
  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // File input ref
  const fileInputRef = useRef(null);

  // Create mutation
  const [createMoleWithImage, { isLoading: isCreating }] = useCreateMoleImageWithChoiceMutation();

  // Helper function to detect orientation from body part code
  const detectOrientation = useCallback(
    (bodyPartCode) => {
      if (!bodyPartCode) return '';

      // Check if it's a back body part (usually ends with _back)
      if (bodyPartCode.includes('_back') || bodyPartCode.includes('back')) {
        return 'back';
      }

      // Check in front body parts
      const frontParts = bodyParts.front || {};
      for (const category in frontParts) {
        if (Array.isArray(frontParts[category])) {
          const found = frontParts[category].find((part) => part.code === bodyPartCode);
          if (found) return 'front';
        }
      }

      // Check in back body parts
      const backParts = bodyParts.back || {};
      for (const category in backParts) {
        if (Array.isArray(backParts[category])) {
          const found = backParts[category].find((part) => part.code === bodyPartCode);
          if (found) return 'back';
        }
      }

      // Default to front if can't determine
      return 'front';
    },
    [bodyParts],
  );

  // Reset form when modal opens/closes
  React.useEffect(() => {
    if (show) {
      const detectedOrientation = selectedBodyPart ? detectOrientation(selectedBodyPart) : '';

      setFormData({
        name: '',
        orientation: detectedOrientation,
        body_part: selectedBodyPart || '',
        file: null,
        distinctive_features: '',
        description: '',
        approximate_size_mm: '',
      });
      setErrors({});
      setImagePreview(null);
    }
  }, [show, selectedBodyPart, bodyParts, detectOrientation]);

  // Handle form field changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Special handling for size field - only allow numbers
    if (name === 'approximate_size_mm') {
      // Allow empty string or positive numbers with decimals
      if (value === '' || /^\d*\.?\d*$/.test(value)) {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }));
      }
    } else if (name === 'orientation') {
      // When orientation changes, clear the body part selection
      setFormData((prev) => ({
        ...prev,
        orientation: value,
        body_part: '', // Clear body part when orientation changes
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrors((prev) => ({ ...prev, file: t('skinMoles.createMole.errors.invalidFileType') }));
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setErrors((prev) => ({ ...prev, file: t('skinMoles.createMole.errors.fileTooLarge') }));
        return;
      }

      setErrors((prev) => ({ ...prev, file: null }));
      
      // Show cropper instead of directly setting file
      setSelectedFile(file);
      setShowCropper(true);
    }
  };

  // Remove selected image
  const handleRemoveImage = () => {
    setFormData((prev) => ({ ...prev, file: null }));
    setImagePreview(null);
    setErrors((prev) => ({ ...prev, file: null }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCropComplete = (croppedFile) => {
    setFormData((prev) => ({ ...prev, file: croppedFile }));
    setShowCropper(false);
    setSelectedFile(null);
    
    // Create preview for cropped image
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(croppedFile);
  };

  const handleCropperClose = () => {
    setShowCropper(false);
    setSelectedFile(null);
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('skinMoles.createMole.errors.nameRequired');
    }

    if (!selectedBodyPart && !formData.orientation) {
      newErrors.orientation = t('skinMoles.createMole.errors.orientationRequired');
    }

    if (!formData.body_part) {
      newErrors.body_part = t('skinMoles.createMole.errors.bodyPartRequired');
    }

    if (!formData.file) {
      newErrors.file = t('skinMoles.createMole.errors.imageRequired');
    }

    // Validate approximate size if provided
    if (formData.approximate_size_mm && parseFloat(formData.approximate_size_mm) <= 0) {
      newErrors.approximate_size_mm = t('skinMoles.createMole.errors.sizeGreaterThanZero');
    }

    if (formData.approximate_size_mm && parseFloat(formData.approximate_size_mm) > 100) {
      newErrors.approximate_size_mm = t('skinMoles.createMole.errors.sizeTooLarge');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Map language code to language_id: 'pl' = 1, 'en' = 2
    // Use same logic as SkinMolesComponent for consistency
    const currentLang = i18n.language;
    const isPolish = String(currentLang || '').toLowerCase().startsWith('pl');
    const languageId = isPolish ? 1 : 2; // Polish = 1, English = 2
    console.log('🌐 MoleCreationModal - Language mapping:', { 
      i18nLanguage: i18n.language, 
      currentLang,
      isPolish, 
      languageId 
    });

    // Based on Swagger API documentation, we need to structure the data for creating a new mole
    const submitData = {
      // File upload
      file: formData.file,

      // Workflow: Create new mole
      create_new_mole: true,

      // Mole data - try both field names in case API expects different format
      mole_name: formData.name.trim(),
      body_part: formData.body_part, // From Swagger enum

      // Language field - use dynamic language based on user's current language
      // Backend expects 'language' field (not 'language_id') for create_with_mole_choice endpoint
      // language is used for mole_image, and we also need to ensure user_mole gets the same language
      language: languageId.toString(), // Convert to string as backend expects string format
      // Also try mole_language in case backend needs separate field for user_mole
      mole_language: languageId.toString(), // Ensure user_mole also gets the correct language

      // Consent fields (likely required)
      consent: true,
      consent_ai_classification: false, // User can change this later

      // Tags field (might be required, empty array)
      tags: [],
    };

    // Add optional fields if provided
    if (formData.distinctive_features.trim()) {
      submitData.distinctive_features = formData.distinctive_features.trim();
    }

    if (formData.description.trim()) {
      submitData.description = formData.description.trim();
    }

    if (formData.approximate_size_mm) {
      submitData.approximate_size_mm = parseInt(formData.approximate_size_mm); // Swagger shows integer
    }

    try {
      const result = await createMoleWithImage(submitData).unwrap();

      // Notify parent component if callback provided
      if (onMoleCreated) {
        onMoleCreated(result);
      }

      onHide(); // Close modal on success
    } catch (error) {
      // Show more specific error message if available
      const errorMessage =
        error.data?.detail || error.data?.message || t('skinMoles.createMole.errors.createFailed');
      setErrors({ submit: errorMessage });
    }
  };

  // Get body part display name
  const getSelectedBodyPartName = () => {
    const bodyPartCode = formData.body_part;

    // Search in both front and back body parts structure: {front: {category: [{code, name}]}, back: {category: [{code, name}]}}
    // We need to create a flat structure that getBodyPartName expects: {category: [{code, name}]}
    const allBodyParts = {};

    // Merge front body parts
    if (bodyParts.front) {
      Object.keys(bodyParts.front).forEach((category) => {
        if (!allBodyParts[category]) {
          allBodyParts[category] = [];
        }
        allBodyParts[category] = [...allBodyParts[category], ...(bodyParts.front[category] || [])];
      });
    }

    // Merge back body parts
    if (bodyParts.back) {
      Object.keys(bodyParts.back).forEach((category) => {
        if (!allBodyParts[category]) {
          allBodyParts[category] = [];
        }
        allBodyParts[category] = [...allBodyParts[category], ...(bodyParts.back[category] || [])];
      });
    }

    return getBodyPartName(bodyPartCode, allBodyParts, bodyPartsLoading, i18n.language);
  };

  // Reset form when modal opens/closes or body parts change
  React.useEffect(() => {
    if (show) {
      const detectedOrientation = selectedBodyPart ? detectOrientation(selectedBodyPart) : '';

      setFormData((prev) => ({
        ...prev,
        orientation: detectedOrientation,
        body_part: selectedBodyPart || prev.body_part,
      }));
    }
  }, [show, selectedBodyPart, bodyParts, i18n.language, detectOrientation]);

  return (
    <>
    <Modal show={show} onHide={onHide} size='lg' centered>
      <Modal.Header closeButton>
        <Modal.Title>📸 {t('skinMoles.createMole.title')}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          {errors.submit && (
            <Alert variant='danger' className='mb-3'>
              {t('skinMoles.errors.formError')}
            </Alert>
          )}

          <Row>
            {/* Image Upload Section */}
            <Col md={6}>
              <Form.Group className='mb-3'>
                <Form.Label className='fw-bold'>{t('skinMoles.createMole.moleImage')} *</Form.Label>

                {!imagePreview ? (
                  <div className='border-2 border-dashed rounded p-4 text-center'>
                    <div className='mb-3'>
                      <i
                        className='bi bi-camera'
                        style={{ fontSize: '2rem', color: '#6c757d' }}
                      ></i>
                    </div>
                    <Button
                      variant='outline-primary'
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isCreating}
                    >
                      📷 {t('skinMoles.createMole.selectImage')}
                    </Button>
                    <div className='mt-2'>
                      <small className='text-muted'>{t('skinMoles.createMole.imageFormats')}</small>
                    </div>
                  </div>
                ) : (
                  <Card>
                    <Card.Img
                      variant='top'
                      src={imagePreview}
                      style={{ height: '200px', objectFit: 'cover' }}
                    />
                    <Card.Body className='p-2'>
                      <div className='d-flex justify-content-between align-items-center'>
                        <small className='text-muted'>{formData.file?.name}</small>
                        <Button
                          variant='outline-danger'
                          size='sm'
                          onClick={handleRemoveImage}
                          disabled={isCreating}
                        >
                          {t('skinMoles.createMole.removeImage')}
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                )}

                <Form.Control
                  ref={fileInputRef}
                  type='file'
                  accept='image/*'
                  onChange={handleFileChange}
                  className='d-none'
                />

                {errors.file && <div className='text-danger mt-1 small'>{errors.file}</div>}
              </Form.Group>
            </Col>

            {/* Form Fields Section */}
            <Col md={6}>
              <Form.Group className='mb-3'>
                <Form.Label className='fw-bold'>{t('skinMoles.createMole.moleName')} *</Form.Label>
                <Form.Control
                  type='text'
                  name='name'
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={t('skinMoles.createMole.moleNamePlaceholder')}
                  isInvalid={!!errors.name}
                  disabled={isCreating}
                />
                <Form.Control.Feedback type='invalid'>{errors.name}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className='mb-3'>
                <Form.Label className='fw-bold'>{t('skinMoles.createMole.bodyPart')} *</Form.Label>

                {/* Show selected body part when pre-selected from SVG */}
                {selectedBodyPart ? (
                  <div>
                    <div className='border rounded p-2 bg-success bg-opacity-10 border-success d-flex justify-content-between align-items-center'>
                      <div>
                        <strong className='text-success'>✓ {getSelectedBodyPartName()}</strong>
                        <br />
                        <small className='text-muted'>
                          <i className='bi bi-map'></i>{' '}
                          {t('skinMoles.createMole.selectedFromBodyMap')}
                        </small>
                      </div>
                      <Button
                        variant='outline-secondary'
                        size='sm'
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            body_part: '',
                            orientation: '',
                          }));
                          if (onClearBodyPart) {
                            onClearBodyPart();
                          }
                        }}
                        disabled={isCreating}
                      >
                        {t('skinMoles.createMole.change')}
                      </Button>
                    </div>
                    {/* Hidden input to maintain form state */}
                    <input type='hidden' name='body_part' value={formData.body_part} />
                  </div>
                ) : (
                  <>
                    {/* Orientation Selection */}
                    <Form.Group className='mb-3'>
                      <Form.Label className='fw-bold'>
                        {t('skinMoles.createMole.orientation')} *
                      </Form.Label>
                      <Form.Control
                        as='select'
                        name='orientation'
                        value={formData.orientation}
                        onChange={handleInputChange}
                        isInvalid={!!errors.orientation}
                        disabled={isCreating}
                      >
                        <option value=''>{t('skinMoles.createMole.orientationPlaceholder')}</option>
                        <option value='front'>{t('skinMoles.front')}</option>
                        <option value='back'>{t('skinMoles.back')}</option>
                      </Form.Control>
                      <Form.Control.Feedback type='invalid'>
                        {errors.orientation}
                      </Form.Control.Feedback>
                    </Form.Group>

                    {/* Body Part Selection - Only show when orientation is selected */}
                    {formData.orientation && (
                      <Form.Group className='mb-3'>
                        <Form.Label className='fw-bold'>
                          {t('skinMoles.createMole.bodyPart')} *
                        </Form.Label>
                        <Form.Control
                          as='select'
                          name='body_part'
                          value={formData.body_part}
                          onChange={handleInputChange}
                          isInvalid={!!errors.body_part}
                          disabled={isCreating}
                        >
                          <option value=''>{t('skinMoles.createMole.bodyPartPlaceholder')}</option>

                          {/* Show only selected orientation body parts */}
                          {bodyParts[formData.orientation] &&
                            Object.entries(bodyParts[formData.orientation]).map(
                              ([category, parts]) => (
                                <optgroup key={category} label={category}>
                                  {Array.isArray(parts) &&
                                    parts.map((part) => (
                                      <option key={part.code} value={part.code}>
                                        {part.name}
                                      </option>
                                    ))}
                                </optgroup>
                              ),
                            )}
                        </Form.Control>
                        <Form.Control.Feedback type='invalid'>
                          {errors.body_part}
                        </Form.Control.Feedback>
                      </Form.Group>
                    )}
                  </>
                )}
              </Form.Group>

              <Form.Group className='mb-3'>
                <Form.Label className='fw-bold'>
                  {t('skinMoles.createMole.distinctiveFeatures')}
                </Form.Label>
                <Form.Control
                  as='textarea'
                  rows={2}
                  name='distinctive_features'
                  value={formData.distinctive_features}
                  onChange={handleInputChange}
                  placeholder={t('skinMoles.createMole.distinctiveFeaturesPlaceholder')}
                  disabled={isCreating}
                />
                <Form.Text className='text-muted'>
                  {t('skinMoles.createMole.distinctiveFeaturesHelp')}
                </Form.Text>
              </Form.Group>

              <Form.Group className='mb-3'>
                <Form.Label className='fw-bold'>{t('skinMoles.createMole.description')}</Form.Label>
                <Form.Control
                  as='textarea'
                  rows={3}
                  name='description'
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder={t('skinMoles.createMole.descriptionPlaceholder')}
                  disabled={isCreating}
                />
                <Form.Text className='text-muted'>
                  {t('skinMoles.createMole.descriptionHelp')}
                </Form.Text>
              </Form.Group>

              <Form.Group className='mb-3'>
                <Form.Label className='fw-bold'>
                  {t('skinMoles.createMole.approximateSize')}
                </Form.Label>
                <div className='input-group'>
                  <Form.Control
                    type='text'
                    name='approximate_size_mm'
                    value={formData.approximate_size_mm}
                    onChange={handleInputChange}
                    placeholder={t('skinMoles.createMole.approximateSizePlaceholder')}
                    isInvalid={!!errors.approximate_size_mm}
                    disabled={isCreating}
                  />
                  <span className='input-group-text'>
                    {t('skinMoles.createMole.approximateSizeUnit')}
                  </span>
                </div>
                <Form.Control.Feedback type='invalid'>
                  {errors.approximate_size_mm}
                </Form.Control.Feedback>
                <Form.Text className='text-muted'>
                  {t('skinMoles.createMole.approximateSizeHelp')}
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant='secondary' onClick={onHide} disabled={isCreating}>
          {t('skinMoles.createMole.cancel')}
        </Button>
        <Button
          variant='primary'
          onClick={handleSubmit}
          disabled={isCreating || !formData.file || !formData.name.trim()}
          title={`Debug: isCreating=${isCreating}, hasFile=${!!formData.file}, hasName=${!!formData.name.trim()}, bodyPart=${!!formData.body_part}`}
        >
          {isCreating ? (
            <>
              <span className='spinner-border spinner-border-sm me-2' role='status' />
              {t('skinMoles.createMole.creating')}
            </>
          ) : (
            `✨ ${t('skinMoles.createMole.create')}`
          )}
        </Button>
      </Modal.Footer>
    </Modal>
    
    {/* Image Cropper Modal */}
    <ImageCropper
      show={showCropper}
      onHide={handleCropperClose}
      imageFile={selectedFile}
      onCropComplete={handleCropComplete}
    />
  </>
  );
};

export default MoleCreationModal;
